import { PromiseCache } from '@zajno/common/structures/promiseCache';
import { SupabaseClient, wrapSupabase } from '../types/client.ts';
import { ProductData } from '../types/models/product.ts';
import { PurchaseRawData, PurchaseStatus } from 'common/types/models/purchase.ts';

export type PurchasesFilters = {
    id?: string;
    status?: PurchaseStatus;
    userId?: string;
    email?: string;
    productId?: string;

    pages?: {
        offset?: number;
        limit?: number;
    };
};

export class ProductsRepository {

    private _cacheById: PromiseCache<ProductData | null | undefined, string> | null = null;
    private _cacheBySlug: PromiseCache<ProductData | null | undefined, string> | null = null;

    constructor(protected readonly client: SupabaseClient) { }

    public useCaching(use: boolean) {
        this._cacheById = use
            ? new PromiseCache(id => this._getProduct(id))
            : null;
        this._cacheBySlug = use
            ? new PromiseCache(slug => this._getProductBySlug(slug))
            : null;
        return this;
    }

    public async getProducts() {
        const results = await wrapSupabase(this.client.from('products').select().order('order_index')) as ProductData[];

        results.forEach(p => {
            this._cacheById?.updateValueDirectly(p.id, p);
            this._cacheBySlug?.updateValueDirectly(p.slug, p);
        });

        return results;
    }

    public getProduct(id: string) {
        return this._cacheById?.get(id) ?? this._getProduct(id);
    }

    public getProductBySlug(slug: string) {
        return this._cacheBySlug?.get(slug) ?? this._getProductBySlug(slug);
    }


    private _getProduct(id: string): Promise<ProductData | null | undefined> {
        return wrapSupabase(this.client.from('products').select().eq('id', id).maybeSingle());
    }

    private _getProductBySlug(slug: string): Promise<ProductData | null | undefined> {
        return wrapSupabase(this.client.from('products').select().eq('slug', slug).maybeSingle());
    }

    public async getPurchases(args?: PurchasesFilters) {

        let q = this.client
            .from('purchases')
            .select<'*', PurchaseRawData>('*')
            .order('created_at', { ascending: false });

        if (args?.id) {
            q = q.eq('id', args.id);
        }
        if (args?.status) {
            q = q.eq('status', args.status);
        }
        if (args?.userId) {
            q = q.eq('user_id', args.userId);
        }
        if (args?.email) {
            q = q.eq('email', args.email);
        }
        if (args?.productId) {
            q = q.eq('product_id', args.productId);
        }

        if (args?.pages?.offset != null) {
            q = q.range(args.pages.offset, args.pages.offset + ((Math.max(1, args.pages.limit ?? 100)) - 1));
        } else if (args?.pages?.limit) {
            q = q.limit(args.pages.limit);
        }

        const results = await wrapSupabase(q);
        return results;
    }
}

type PurchaseCreateData = Pick<PurchaseRawData, 'email' | 'input_data' | 'product_id' | 'user_id' | 'stripe' >
    & Partial<Pick<PurchaseRawData, 'status'>>;
type PurchaseUpdateData = Pick<Partial<PurchaseRawData>, 'email' | 'input_data' | 'product_id' | 'status' | 'status_history' | 'user_id' | 'stripe' >;

export class ServerProductsRepository extends ProductsRepository {

    public async createPurchase(args: PurchaseCreateData): Promise<PurchaseRawData | null | undefined> {
        const result = await wrapSupabase(
            this.client
                .from('purchases')
                .insert({
                    email: args.email,
                    product_id: args.product_id,
                    stripe: args.stripe,
                    user_id: args.user_id,
                    status: args.status || PurchaseStatus.Initiated,
                    input_data: args.input_data,
                })
                .select<'*', PurchaseRawData>()
                .single(),
        );

        return result;
    }

    public getPurchase(id: string): Promise<PurchaseRawData | null | undefined> {
        return wrapSupabase(
            this.client.from('purchases')
                .select()
                .eq('id', id)
                .maybeSingle(),
        );
    }

    public async updatePurchase(id: string, args: PurchaseUpdateData): Promise<PurchaseRawData | null | undefined> {
        const result = await wrapSupabase(
            this.client.from('purchases')
                .update(args)
                .eq('id', id)
                .select<'*', PurchaseRawData>()
                .single(),
        );

        return result;
    }
}
